import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash, Plus, UploadSimple } from "@phosphor-icons/react";
import { SEVERITY_STYLE } from "./FraudScoreBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FraudRulesTable() {
  const [rules, setRules] = useState([]);
  const load = useCallback(() => axios.get(`${API}/fraud/rules`).then(r => setRules(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (rule, is_active) => {
    try {
      await axios.put(`${API}/fraud/rules/${rule.rule_id}`, { is_active });
      setRules(prev => prev.map(r => r.rule_id === rule.rule_id ? { ...r, is_active } : r));
      toast.success(`${rule.rule_id} ${is_active ? "enabled" : "disabled"}`);
    } catch { toast.error("Update failed"); }
  };
  const setSeverity = async (rule, severity) => {
    try {
      await axios.put(`${API}/fraud/rules/${rule.rule_id}`, { severity });
      setRules(prev => prev.map(r => r.rule_id === rule.rule_id ? { ...r, severity } : r));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="fraud-rules-table">
      <table className="w-full text-xs">
        <thead><tr className="bg-gray-50 border-b border-gray-200">
          <th className="px-3 py-2 text-left font-semibold text-gray-500">Rule</th>
          <th className="px-3 py-2 text-left font-semibold text-gray-500">Category</th>
          <th className="px-3 py-2 text-left font-semibold text-gray-500">Severity</th>
          <th className="px-3 py-2 text-left font-semibold text-gray-500">Config</th>
          <th className="px-3 py-2 text-center font-semibold text-gray-500">Active</th>
        </tr></thead>
        <tbody>
          {rules.map(r => (
            <tr key={r.rule_id} className={`border-b border-gray-100 ${!r.is_active ? "opacity-50" : ""}`} data-testid={`rule-row-${r.rule_id}`}>
              <td className="px-3 py-2"><span className="font-mono text-gray-400 mr-2">{r.rule_id}</span><span className="font-medium text-gray-900">{r.name}</span></td>
              <td className="px-3 py-2 capitalize text-gray-600">{r.category}</td>
              <td className="px-3 py-2">
                <select value={r.severity} onChange={e => setSeverity(r, e.target.value)} className={`text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 ${SEVERITY_STYLE[r.severity]}`} data-testid={`rule-severity-${r.rule_id}`}>
                  {["critical", "high", "medium", "low"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-3 py-2 font-mono text-gray-500 truncate max-w-[200px]">{Object.entries(r.config || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}</td>
              <td className="px-3 py-2 text-center"><Switch checked={!!r.is_active} onCheckedChange={v => toggle(r, v)} data-testid={`rule-toggle-${r.rule_id}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FraudPricesTable() {
  const [prices, setPrices] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ generic_name: "", item_name: "", nppa_ceiling_price: "", unit: "tablet" });
  const load = useCallback(() => axios.get(`${API}/fraud/prices`, { params: { q } }).then(r => setPrices(r.data)).catch(() => {}), [q]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.generic_name || !form.nppa_ceiling_price) return toast.error("Generic name and price are required");
    try {
      await axios.post(`${API}/fraud/prices`, form);
      setForm({ generic_name: "", item_name: "", nppa_ceiling_price: "", unit: "tablet" });
      toast.success("Reference price saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (g) => { await axios.delete(`${API}/fraud/prices/${encodeURIComponent(g)}`); load(); };
  const importCsv = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await axios.post(`${API}/fraud/prices/import`, fd); toast.success(`Imported ${r.data.imported} prices`); load(); }
    catch { toast.error("Import failed"); }
  };

  return (
    <div className="space-y-3" data-testid="fraud-prices-panel">
      <div className="flex gap-2 items-center">
        <Input placeholder="Search medicine…" value={q} onChange={e => setQ(e.target.value)} className="h-8 text-xs max-w-xs" data-testid="price-search" />
        <label className="ml-auto text-xs text-[#002FA7] cursor-pointer inline-flex items-center gap-1 hover:underline">
          <UploadSimple size={14} /> Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={e => e.target.files[0] && importCsv(e.target.files[0])} data-testid="price-import-input" />
        </label>
      </div>
      <div className="grid grid-cols-5 gap-2 bg-white border border-gray-200 rounded-md p-3">
        <Input placeholder="generic name *" value={form.generic_name} onChange={e => setForm({ ...form, generic_name: e.target.value })} className="h-8 text-xs" data-testid="price-generic-input" />
        <Input placeholder="item name" value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} className="h-8 text-xs" data-testid="price-item-input" />
        <Input placeholder="ceiling ₹ *" type="number" value={form.nppa_ceiling_price} onChange={e => setForm({ ...form, nppa_ceiling_price: e.target.value })} className="h-8 text-xs" data-testid="price-amount-input" />
        <Input placeholder="unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="h-8 text-xs" data-testid="price-unit-input" />
        <Button size="sm" onClick={add} className="h-8 bg-[#002FA7] text-white text-xs gap-1" data-testid="price-add-btn"><Plus size={12} /> Add</Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Item</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Generic</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-500">Ceiling (₹)</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Unit</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500">Source</th>
            <th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {prices.map(p => (
              <tr key={p.generic_name} className="border-b border-gray-100" data-testid={`price-row-${p.generic_name}`}>
                <td className="px-3 py-1.5 font-medium text-gray-900">{p.item_name}</td>
                <td className="px-3 py-1.5 text-gray-600">{p.generic_name}</td>
                <td className="px-3 py-1.5 text-right font-mono">{Number(p.nppa_ceiling_price).toFixed(2)}</td>
                <td className="px-3 py-1.5 text-gray-600">{p.unit}</td>
                <td className="px-3 py-1.5 text-gray-400">{p.source}</td>
                <td className="px-3 py-1.5 text-right"><button onClick={() => remove(p.generic_name)} className="text-red-500 hover:text-red-700" data-testid={`price-delete-${p.generic_name}`}><Trash size={13} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FraudTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [file, setFile] = useState(null);
  const load = useCallback(() => axios.get(`${API}/fraud/templates`).then(r => setTemplates(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name || !file) return toast.error("Hospital name and a letterhead image are required");
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/fraud/templates`, fd, { params: { hospital_name: name, hospital_code: code } });
      toast.success("Provider template registered"); setName(""); setCode(""); setFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const remove = async (h) => { await axios.delete(`${API}/fraud/templates/${encodeURIComponent(h)}`); load(); };

  return (
    <div className="space-y-3" data-testid="fraud-templates-panel">
      <div className="grid grid-cols-4 gap-2 bg-white border border-gray-200 rounded-md p-3 items-center">
        <Input placeholder="Hospital name *" value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" data-testid="tpl-name-input" />
        <Input placeholder="Hospital code" value={code} onChange={e => setCode(e.target.value)} className="h-8 text-xs" data-testid="tpl-code-input" />
        <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0] || null)} className="text-xs" data-testid="tpl-file-input" />
        <Button size="sm" onClick={add} className="h-8 bg-[#002FA7] text-white text-xs gap-1" data-testid="tpl-add-btn"><Plus size={12} /> Register letterhead</Button>
      </div>
      <p className="text-[11px] text-gray-500">Upload a genuine letterhead page image (JPG/PNG). The engine fingerprints the header region and flags claim pages from this hospital whose letterhead differs.</p>
      {templates.length === 0 ? (
        <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md p-4 text-center" data-testid="tpl-empty">No provider templates registered yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {templates.map(t => (
            <div key={t.hospital_name} className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3" data-testid={`tpl-row-${t.hospital_name}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{t.hospital_name}</p>
                <p className="text-[10px] font-mono text-gray-400 truncate">{t.hospital_code || "—"} · {t.letterhead_phash?.slice(0, 16)}…</p>
              </div>
              <Badge variant="outline" className="text-[10px]">{t.sample_file}</Badge>
              <button onClick={() => remove(t.hospital_name)} className="text-red-500 hover:text-red-700" data-testid={`tpl-delete-${t.hospital_name}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
